/**
 * ===========================================
 * BRANDING & CONFIGURATION
 * ===========================================
 * 
 * Edit this file to customize the application's
 * branding, colors, and digital wallet settings.
 * 
 * To switch themes, change ACTIVE_THEME below.
 */

// ===========================================
// ACTIVE THEME SELECTOR
// ===========================================
// Change this to switch between themes: 'idv' or 'mounties'
const ACTIVE_THEME = 'mounties';

// ===========================================
// THEME DEFINITIONS
// ===========================================
const themes = {
  // =========================================
  // IDV PACIFIC THEME
  // =========================================
  idv: {
    brand: {
      name: 'IDV Pacific',
      tagline: 'Identity Verification',
      logo: 'https://idvpacific.com.au/wp-content/uploads/2018/09/IVD-Logo-retina.png',
      logoAlt: 'IDV Pacific Logo',
      favicon: 'https://idvpacific.com.au/wp-content/uploads/2019/08/cropped-Favicon.png',
    },
    litecard: {
      templateId: 'eyaEuEx6TO02LOb5rFx-o',
    },
    colors: {
      // Primary brand color
      primary: '#006abc',
      primaryHover: '#005a9e',
      primaryLight: '#e3f2fd',
      
      // Logo background (for transparent logos)
      logoBackground: '#ffffff',
      logoPadding: '8px 16px',
      logoBorderRadius: '6px',
      
      // Navbar and Footer
      navbarBackground: '#006abc',
      navbarText: '#ffffff',
      footerBackground: '#006abc',
      footerText: '#ffffff',
      
      // Secondary color
      secondary: '#6c757d',
      secondaryHover: '#545b62',
      
      // Status colors
      success: '#28a745',
      successLight: '#d4edda',
      successBorder: '#c3e6cb',
      
      error: '#dc3545',
      errorLight: '#f8d7da',
      errorBorder: '#f5c6cb',
      
      warning: '#ffc107',
      warningLight: '#fff3cd',
      warningBorder: '#ffeeba',
      warningText: '#856404',
      
      // Background colors
      background: '#f5f7fa',
      backgroundCard: '#ffffff',
      backgroundDark: '#1a1a2e',
      
      // Text colors
      textPrimary: '#333333',
      textSecondary: '#666666',
      textMuted: '#999999',
      textLight: '#ffffff',
      
      // Border colors
      border: '#e0e0e0',
      borderLight: '#f0f0f0',
      
      // Button colors
      buttonPrimary: '#006abc',
      buttonPrimaryHover: '#005a9e',
      buttonText: '#ffffff',
      
      // Accent colors
      accent1: '#006abc',
      accent2: '#004a8c',
    },
  },

  // =========================================
  // MOUNTIES THEME
  // =========================================
  mounties: {
    brand: {
      name: 'Mounties',
      tagline: 'Member Verification',
      logo: '/mounties-logo.png',
      logoAlt: 'Mounties Logo',
      favicon: 'https://mountiesvenue.s3.ap-southeast-2.amazonaws.com/stores/_images/mounties-fav-new.png',
    },
    litecard: {
      templateId: 'BbIWw-cm2RJxcaC4W5NTq',
    },
    colors: {
      // Primary brand color (golden/tan for buttons)
      primary: '#edc494',
      primaryHover: '#d4a86e',
      primaryLight: '#fdf6ed',
      
      // Logo background (transparent for Mounties)
      logoBackground: 'transparent',
      logoPadding: '0',
      logoBorderRadius: '0',
      
      // Navbar and Footer (dark navy)
      navbarBackground: '#161825',
      navbarText: '#ffffff',
      footerBackground: '#161825',
      footerText: '#ffffff',
      
      // Secondary color
      secondary: '#6c757d',
      secondaryHover: '#545b62',
      
      // Status colors
      success: '#28a745',
      successLight: '#d4edda',
      successBorder: '#c3e6cb',
      
      error: '#dc3545',
      errorLight: '#f8d7da',
      errorBorder: '#f5c6cb',
      
      warning: '#ffc107',
      warningLight: '#fff3cd',
      warningBorder: '#ffeeba',
      warningText: '#856404',
      
      // Background colors
      background: '#f5f7fa',
      backgroundCard: '#ffffff',
      backgroundDark: '#161825',
      
      // Text colors
      textPrimary: '#333333',
      textSecondary: '#666666',
      textMuted: '#999999',
      textLight: '#ffffff',
      
      // Border colors
      border: '#e0e0e0',
      borderLight: '#f0f0f0',
      
      // Button colors (golden/tan with dark text)
      buttonPrimary: '#edc494',
      buttonPrimaryHover: '#d4a86e',
      buttonText: '#161825',
      
      // Accent colors
      accent1: '#edc494',
      accent2: '#161825',
    },
  },
};

// ===========================================
// GET ACTIVE THEME CONFIG
// ===========================================
const activeTheme = themes[ACTIVE_THEME] || themes.idv;

const config = {
  // Current theme name
  themeName: ACTIVE_THEME,
  
  // Brand identity from active theme
  brand: activeTheme.brand,
  
  // Colors from active theme
  colors: activeTheme.colors,

  // ===========================================
  // LITECARD / DIGITAL WALLET CONFIGURATION
  // ===========================================
  litecard: {
    apiBase: 'https://bff-api.demo.litecard.io/api/v1',
    credentials: {
      username: 'idv-demo-brand@litecard.com.au',
      password: 'e14j83coPYSxhRH3hK7$',
    },
    templateId: activeTheme.litecard?.templateId || 'BbIWw-cm2RJxcaC4W5NTq',
  },

  // ===========================================
  // DIGITAL ID CARD PAYLOAD
  // ===========================================
  getCardPayload: (documentData, verificationId) => {
    const nameParts = (documentData?.fullName || 'Test User').split(' ');
    const firstName = nameParts[0] || 'Test';
    const lastName = nameParts.slice(1).join(' ') || 'User';
    
    // IDV Theme payload (simpler - just name, email, DOB)
    if (ACTIVE_THEME === 'idv') {
      return {
        templateId: activeTheme.litecard?.templateId || 'eyaEuEx6TO02LOb5rFx-o',
        cardPayload: {
          firstName: firstName,
          lastName: lastName,
          email: 'demo@idvpacific.com.au',
          dateOfBirth: documentData?.dateOfBirth ? new Date(documentData.dateOfBirth).toISOString() : new Date().toISOString(),
        },
        options: {
          downloadId: null,
          emailInvitationEnabled: true,
          smsInvitationEnabled: false,
        },
      };
    }
    
    // Mounties Theme payload (with memberId, pointsKey, membershipType)
    const memberId = verificationId 
      ? parseInt(verificationId.substring(0, 5), 16) % 100000 
      : Math.floor(Math.random() * 100000);
    const pointsKey = Math.floor(Date.now() / 1000) % 100000;
    
    return {
      templateId: activeTheme.litecard?.templateId || 'BbIWw-cm2RJxcaC4W5NTq',
      cardPayload: {
        firstName: firstName,
        lastName: lastName,
        email: 'demo@idvpacific.com.au',
        phone: '+611234567890',
        memberId: memberId,
        pointsKey: pointsKey,
        membershipType: 'Verified Member',
      },
      options: {
        downloadId: null,
        emailInvitationEnabled: true,
        smsInvitationEnabled: false,
      },
    };
  },

  // ===========================================
  // API ENDPOINTS
  // ===========================================
  api: {
    faceCompare: 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/FaceCompare',
    getUploadUrl: 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/GetUploadUrl',
    signatureDetection: 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/SignatureDetection',
    documentExtraction: 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/ExtractDocumentData',
  },

  // ===========================================
  // VERIFICATION SETTINGS
  // ===========================================
  verification: {
    faceMatchThreshold: 80,
    livenessConfidenceMin: 70,
    minimumAge: 18,
  },

  // ===========================================
  // QR CODE SETTINGS
  // ===========================================
  qrCode: {
    size: 300,
    displaySize: 220,
  },

  // ===========================================
  // UI TEXT / LABELS
  // ===========================================
  text: {
    appTitle: 'Identity Verification',
    verifyButton: 'Start Verification',
    resetButton: 'Verify Another Person',
    
    statusVerified: 'All verification checks passed.',
    statusFailed: 'Verification failed.',
    statusProcessing: 'Processing verification data...',
    statusWarning: 'Warning: Selfie and Face match verification skipped.',
    
    sectionChecks: 'VERIFICATION CHECKS',
    sectionImages: 'SUBMITTED IMAGES',
    sectionPersonal: 'PERSONAL INFORMATION',
    sectionDatabase: 'DATABASE VERIFICATION',
    sectionBiometric: 'BIOMETRIC VERIFICATION',
    sectionSignature: 'SIGNATURE DETECTION',
    sectionWallet: 'ADD TO DIGITAL WALLET',
    
    walletDescription: 'Scan the QR code below to add your verified ID to your mobile wallet',
    appleWallet: 'Apple Wallet',
    googleWallet: 'Google Wallet',
  },
};

export default config;
